import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuth, refreshThunk, selectUser, selectIsFeatureEnabled } from './store/authSlice';
import { setTenantI18nConfig, resetI18n, fetchI18nConfigThunk } from './store/i18nSlice';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { LoadingPage } from './components/common/UIComponents';
import { isNativeMobileRuntime } from './utils/nativeRuntime';

// Lazy-loaded pages  
const HomePage      = lazy(() => import('./pages/HomePage'));
const Login         = lazy(() => import('./pages/Login'));
const Unauthorized  = lazy(() => import('./pages/Unauthorized'));
const ModuleSelect  = lazy(() => import('./pages/ModuleSelect'));
const RemoteBarcodeScanner = lazy(() => import('./pages/RemoteBarcodeScanner'));

// Dashboards
const Dashboard     = lazy(() => import('./pages/dashboards/Dashboard'));

// Healthcare
const PatientList       = lazy(() => import('./pages/healthcare/PatientList'));
const PatientDetail     = lazy(() => import('./pages/healthcare/PatientDetail'));
const AppointmentList   = lazy(() => import('./pages/healthcare/AppointmentList'));
const DoctorList        = lazy(() => import('./pages/healthcare/DoctorList'));
const HealthcareReports = lazy(() => import('./pages/healthcare/HealthcareReports'));
const HCInvoices        = lazy(() => import('./pages/healthcare/HCInvoices'));

// Factory
const FactoryOrderList    = lazy(() => import('./pages/factory/FactoryOrderList'));
const FactoryProductList = lazy(() => import('./pages/factory/FactoryProductList'));
const FactoryVariantCatalog = lazy(() => import('./pages/factory/FactoryVariantCatalog'));
const FactoryVariantOrderBuilder = lazy(() => import('./pages/factory/FactoryVariantOrderBuilder'));
const FactoryDecimalInventoryPage = lazy(() => import('./pages/factory/FactoryDecimalInventoryPage'));
const DeliveryTracking    = lazy(() => import('./pages/factory/DeliveryTracking'));
const FactoryReports      = lazy(() => import('./pages/factory/FactoryReports'));
const FactoryCustomers    = lazy(() => import('./pages/factory/FactoryCustomers'));
const FactoryPartnerCatalog = lazy(() => import('./components/factory/PartnerOrderingWorkspace'));
const FactoryPartnerOrderRequests = lazy(() => import('./pages/factory/FactoryPartnerOrderRequests'));

// Billing
const BillingInvoices  = lazy(() => import('./pages/billing/BillingInvoices'));
const DailyExpenses    = lazy(() => import('./pages/billing/DailyExpenses'));

// Reports
const Reports          = lazy(() => import('./pages/reports/Reports'));
const ProfitLossReport = lazy(() => import('./pages/reports/ProfitLossReport'));

// Pharmacy
const MedicineList     = lazy(() => import('./pages/pharmacy/MedicineList'));
const MedicineRacks    = lazy(() => import('./pages/pharmacy/MedicineRacks'));
const PharmacyBilling  = lazy(() => import('./pages/pharmacy/PharmacyBilling'));
const PharmacyReports  = lazy(() => import('./pages/pharmacy/PharmacyReports'));
const SupplierList     = lazy(() => import('./pages/pharmacy/SupplierList'));
const PurchaseOrderList= lazy(() => import('./pages/pharmacy/PurchaseOrderList'));
const PharmacyCustomers= lazy(() => import('./pages/pharmacy/PharmacyCustomers'));
const PharmacyReturns  = lazy(() => import('./pages/pharmacy/PharmacyReturns'));
const MedicineOrders   = lazy(() => import('./pages/pharmacy/MedicineOrders'));
const PharmacySettings = lazy(() => import('./pages/pharmacy/PharmacySettings'));

// Super Admin
const TenantManagement    = lazy(() => import('./pages/superadmin/TenantManagement'));
const UserManagement      = lazy(() => import('./pages/superadmin/UserManagement'));
const SubscriptionReport  = lazy(() => import('./pages/superadmin/SubscriptionReport'));
const SubscriptionPricing = lazy(() => import('./pages/superadmin/SubscriptionPricing'));
const ContactMessages     = lazy(() => import('./pages/superadmin/ContactMessages'));
const MfaSetup            = lazy(() => import('./pages/superadmin/MfaSetup'));
const AuditLog            = lazy(() => import('./pages/superadmin/AuditLog'));
const RenewalRequests     = lazy(() => import('./pages/superadmin/RenewalRequests'));
const RoleMatrix          = lazy(() => import('./pages/superadmin/RoleMatrix'));
const FinancialAccounts        = lazy(() => import('./pages/superadmin/FinancialAccounts'));
const MasterTypes              = lazy(() => import('./pages/superadmin/MasterTypes'));
const Testimonials             = lazy(() => import('./pages/superadmin/Testimonials'));
const TenantChartOfAccounts    = lazy(() => import('./pages/settings/TenantChartOfAccounts'));
const TenantMasterTypes        = lazy(() => import('./pages/settings/TenantMasterTypes'));
const TenantTerminology        = lazy(() => import('./pages/settings/TenantTerminology'));
const LanguagePreferences      = lazy(() => import('./pages/settings/LanguagePreferences'));
const MyReferral               = lazy(() => import('./pages/settings/MyReferral'));
const TenantDataCleanup        = lazy(() => import('./pages/settings/TenantDataCleanup'));
const BarcodeQrGenerator       = lazy(() => import('./pages/settings/BarcodeQrGenerator'));
const ReferralManagement       = lazy(() => import('./pages/superadmin/ReferralManagement'));

// Workforce
const WorkerList        = lazy(() => import('./pages/workforce/WorkerList'));
const WorkerDetail      = lazy(() => import('./pages/workforce/WorkerDetail'));
const AttendanceSheet   = lazy(() => import('./pages/workforce/AttendanceSheet'));
const ProductionLog     = lazy(() => import('./pages/workforce/ProductionLog'));
const MachineProductionLog = lazy(() => import('./pages/factory/MachineProductionLog'));
const WorkerPayments    = lazy(() => import('./pages/workforce/WorkerPayments'));
const VendorList        = lazy(() => import('./pages/workforce/VendorList'));
const ResourceInventory = lazy(() => import('./pages/workforce/ResourceInventory'));
const EquipmentTracker  = lazy(() => import('./pages/workforce/EquipmentTracker'));
const WorkforceReports  = lazy(() => import('./pages/workforce/WorkforceReports'));
const SiteManagement    = lazy(() => import('./pages/workforce/SiteManagement'));

function getDefaultAppRoute(role) {
  return role === 'PARTNER' || role === 'MARKETING'
    ? '/factory/partner-catalog'
    : '/dashboard';
}

/**
 * After login redirect: SUPER_ADMIN → tenants page, others → modules.
 * Used only inside protected app shell.
 */
function AppRedirect() {
  const isAuth = useSelector(selectIsAuth);
  const role   = useSelector(state => state.auth.user?.role);
  if (!isAuth) return <Navigate to="/login" replace />;
  if (role === 'SUPER_ADMIN') return <Navigate to="/super-admin/tenants" replace />;
  if (role === 'PARTNER' || role === 'MARKETING') {
    return <Navigate to="/factory/partner-catalog" replace />;
  }
  return <Navigate to="/modules" replace />;
}

export default function App() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const referralEnabled = useSelector(selectIsFeatureEnabled('REFERRAL'));
  const workforceEnabled = useSelector(selectIsFeatureEnabled('WORKFORCE'));
  const businessSetupEnabled = useSelector(selectIsFeatureEnabled('BUSINESS_SETUP'));
  const terminologyControlsEnabled = useSelector(selectIsFeatureEnabled('TERMINOLOGY_CONTROLS'));
  const barcodeGeneratorEnabled = useSelector(selectIsFeatureEnabled('BARCODE_GENERATOR'));
  const qrGeneratorEnabled = useSelector(selectIsFeatureEnabled('QR_GENERATOR'));
  const legacyBarcodeQrGeneratorEnabled = useSelector(selectIsFeatureEnabled('BARCODE_QR_GENERATOR'));
  const barcodeQrGeneratorEnabled = barcodeGeneratorEnabled || qrGeneratorEnabled || legacyBarcodeQrGeneratorEnabled;
  const [bootstrapped, setBootstrapped] = useState(false);
  const defaultAppRoute = getDefaultAppRoute(user?.role);
  const I18N_SYNC_INTERVAL_MS = 60000;

  // On every page load, try to restore the access token from the httpOnly cookie.
  // If the cookie is expired/absent, refreshThunk will fail and user stays logged out.
  useEffect(() => {
    dispatch(refreshThunk()).finally(() => setBootstrapped(true));
  }, [dispatch]);

  useEffect(() => {
    if (!user) {
      dispatch(resetI18n());
      return;
    }

    const fallbackSupportedLanguages = Array.isArray(user.supportedLanguages) && user.supportedLanguages.length > 0
      ? user.supportedLanguages
      : [user.primaryLanguage, user.secondaryLanguage].filter(Boolean);

    dispatch(setTenantI18nConfig({
      i18nEnabled: user.i18nEnabled ?? false,
      supportedLanguages: fallbackSupportedLanguages,
      primaryLanguage: user.primaryLanguage || 'en',
      secondaryLanguage: user.secondaryLanguage || null,
      i18nTranslateMenus: user.i18nTranslateMenus,
      i18nTranslateButtons: user.i18nTranslateButtons,
      i18nTranslateForms: user.i18nTranslateForms,
      i18nTranslateErrors: user.i18nTranslateErrors,
    }));

    dispatch(fetchI18nConfigThunk());
  }, [dispatch, user]);

  useEffect(() => {
    if (!user || user.role === 'SUPER_ADMIN') {
      return;
    }

    const syncI18nConfig = () => {
      dispatch(fetchI18nConfigThunk());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncI18nConfig();
      }
    };

    const intervalId = window.setInterval(syncI18nConfig, I18N_SYNC_INTERVAL_MS);
    window.addEventListener('focus', syncI18nConfig);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncI18nConfig);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, user, I18N_SYNC_INTERVAL_MS]);

  useEffect(() => {
    if (!bootstrapped || !user || !isNativeMobileRuntime()) {
      return;
    }

    const revalidateSession = () => {
      dispatch(refreshThunk()).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        revalidateSession();
      }
    };

    window.addEventListener('focus', revalidateSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', revalidateSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [bootstrapped, dispatch, user]);

  useEffect(() => {
    if (!bootstrapped || !user || !isNativeMobileRuntime()) {
      return;
    }

    const rootRoutes = new Set(['/dashboard', '/modules', defaultAppRoute]);
    const path = window.location.pathname;

    if (rootRoutes.has(path) && !window.history.state?.__uoBackTrap) {
      window.history.pushState({ ...(window.history.state || {}), __uoBackTrap: true }, '', window.location.href);
    }

    const handlePopState = () => {
      if (rootRoutes.has(window.location.pathname)) {
        window.history.pushState({ ...(window.history.state || {}), __uoBackTrap: true }, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [bootstrapped, defaultAppRoute, user]);

  if (!bootstrapped) {
    return <div className="min-h-screen flex items-center justify-center"><LoadingPage /></div>;
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingPage /></div>}>
        <Routes>
          {/* Public */}
          <Route path="/"       element={<HomePage />} />
          <Route path="/app"    element={<AppRedirect />} />
          <Route path="/login"  element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/remote-scanner" element={<RemoteBarcodeScanner />} />

          {/* Protected — all require auth, wrapped in sidebar layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard — all roles */}
            <Route path="dashboard" element={
              <ProtectedRoute roles={['SUPER_ADMIN','ADMIN','DOCTOR','STAFF','MANAGER','READ_ONLY']}>
                <Dashboard />
              </ProtectedRoute>
            } />

            <Route path="modules" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','MANAGER','READ_ONLY','PARTNER','MARKETING']}>
                <ModuleSelect />
              </ProtectedRoute>
            } />

            {/* Super Admin */}
            <Route path="super-admin/tenants" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <TenantManagement />
              </ProtectedRoute>
            } />
            <Route path="super-admin/users" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="super-admin/subscriptions" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <SubscriptionReport />
              </ProtectedRoute>
            } />
            <Route path="super-admin/plan-pricing" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <SubscriptionPricing />
              </ProtectedRoute>
            } />
            <Route path="super-admin/contact-messages" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <ContactMessages />
              </ProtectedRoute>
            } />
            <Route path="super-admin/mfa-setup" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <MfaSetup />
              </ProtectedRoute>
            } />
            {/* MFA enrollment for any authenticated tenant user when tenant-wide MFA is required */}
            <Route path="mfa-setup" element={
              <ProtectedRoute>
                <MfaSetup />
              </ProtectedRoute>
            } />
            <Route path="super-admin/audit-log" element={
              <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN']}>
                <AuditLog />
              </ProtectedRoute>
            } />
            <Route path="super-admin/renewals" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <RenewalRequests />
              </ProtectedRoute>
            } />
            <Route path="super-admin/referrals" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <ReferralManagement />
              </ProtectedRoute>
            } />
            <Route path="super-admin/testimonials" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Testimonials />
              </ProtectedRoute>
            } />
            <Route path="super-admin/role-matrix" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <RoleMatrix />
              </ProtectedRoute>
            } />
            <Route path="super-admin/financial-accounts" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <FinancialAccounts />
              </ProtectedRoute>
            } />
            <Route path="super-admin/master-types" element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <MasterTypes />
              </ProtectedRoute>
            } />

            {/* Tenant ADMIN user management */}
            <Route path="users" element={
              <ProtectedRoute roles={['ADMIN']}>
                <UserManagement />
              </ProtectedRoute>
            } />

            {/* Healthcare */}
            <Route path="healthcare/patients" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="HEALTHCARE">
                <PatientList />
              </ProtectedRoute>
            } />
            <Route path="healthcare/patients/:id" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="HEALTHCARE">
                <PatientDetail />
              </ProtectedRoute>
            } />
            <Route path="healthcare/appointments" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="HEALTHCARE">
                <AppointmentList />
              </ProtectedRoute>
            } />
            <Route path="healthcare/doctors" element={
              <ProtectedRoute roles={['ADMIN']} module="HEALTHCARE">
                <DoctorList />
              </ProtectedRoute>
            } />
            <Route path="healthcare/reports" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="HEALTHCARE">
                <HealthcareReports />
              </ProtectedRoute>
            } />
            <Route path="healthcare/invoices" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="HEALTHCARE">
                <HCInvoices />
              </ProtectedRoute>
            } />

            {/* Factory */}
            <Route path="factory/orders" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="FACTORY">
                <FactoryOrderList />
              </ProtectedRoute>
            } />
            <Route path="factory/customers" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="FACTORY">
                <FactoryCustomers />
              </ProtectedRoute>
            } />
            <Route path="factory/products" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="FACTORY">
                <FactoryProductList />
              </ProtectedRoute>
            } />
            <Route path="factory/variant-catalog" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="FACTORY" feature="FACTORY_PRODUCT_VARIANTS">
                <FactoryVariantCatalog />
              </ProtectedRoute>
            } />
            <Route path="factory/variant-orders" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="FACTORY" feature="FACTORY_PRODUCT_VARIANTS">
                <FactoryVariantOrderBuilder />
              </ProtectedRoute>
            } />
            <Route path="factory/decimal-inventory" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="FACTORY" feature="FACTORY_DECIMAL_INVENTORY">
                <FactoryDecimalInventoryPage />
              </ProtectedRoute>
            } />
            <Route path="factory/deliveries" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="FACTORY">
                <DeliveryTracking />
              </ProtectedRoute>
            } />
            <Route path="factory/reports" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="FACTORY">
                <FactoryReports />
              </ProtectedRoute>
            } />
            <Route path="factory/partner-catalog" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY','PARTNER','MARKETING']} feature="FACTORY_PARTNER_ORDERING">
                <FactoryPartnerCatalog />
              </ProtectedRoute>
            } />
            <Route path="factory/partner-cart" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','PARTNER','MARKETING']} feature="FACTORY_PARTNER_ORDERING">
                <FactoryPartnerCatalog />
              </ProtectedRoute>
            } />
            <Route path="factory/partner-my-orders" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','PARTNER','MARKETING']} feature="FACTORY_PARTNER_ORDERING">
                <FactoryPartnerCatalog />
              </ProtectedRoute>
            } />
            <Route path="factory/partner-order-requests" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="FACTORY">
                <FactoryPartnerOrderRequests />
              </ProtectedRoute>
            } />

            {/* Billing — factory-domain invoicing */}
            <Route path="billing/invoices" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="FACTORY">
                <BillingInvoices />
              </ProtectedRoute>
            } />
            <Route path="billing/daily-expenses" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} feature="DAILY_EXPENSES">
                <DailyExpenses />
              </ProtectedRoute>
            } />

            {/* Reports — factory analytics (healthcare reports are at healthcare/reports) */}
            <Route path="reports" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']}>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="reports/profit-loss" element={
              <ProtectedRoute roles={['ADMIN']}>
                <ProfitLossReport />
              </ProtectedRoute>
            } />

            {/* Pharmacy */}
            <Route path="pharmacy/medicines" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="PHARMACY">
                <MedicineList />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/racks" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']} module="PHARMACY">
                <MedicineRacks />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/invoices" element={
              <ProtectedRoute roles={['ADMIN','DOCTOR','STAFF','READ_ONLY']} module="PHARMACY">
                <PharmacyBilling />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/reports" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','DOCTOR']} module="PHARMACY">
                <PharmacyReports />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/suppliers" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="PHARMACY">
                <SupplierList />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/purchase-orders" element={
              <ProtectedRoute roles={['ADMIN','MANAGER']} module="PHARMACY">
                <PurchaseOrderList />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/customers" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY']} module="PHARMACY">
                <PharmacyCustomers />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/returns" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF']} module="PHARMACY">
                <PharmacyReturns />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/medicine-orders" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY']} module="PHARMACY">
                <MedicineOrders />
              </ProtectedRoute>
            } />
            <Route path="pharmacy/settings" element={
              <ProtectedRoute roles={['ADMIN']} module="PHARMACY">
                <PharmacySettings />
              </ProtectedRoute>
            } />

            {/* Workforce */}
            <Route path="workforce/workers" element={
              workforceEnabled ? (
                <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']}>
                  <WorkerList />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />
            <Route path="workforce/workers/:id" element={
              workforceEnabled ? (
                <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']}>
                  <WorkerDetail />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />
            <Route path="workforce/attendance" element={
              workforceEnabled ? (
                <ProtectedRoute roles={['ADMIN','MANAGER','READ_ONLY']}>
                  <AttendanceSheet />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />
            <Route path="workforce/production" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','READ_ONLY']}>
                <ProductionLog />
              </ProtectedRoute>
            } />
            <Route path="factory/machine-production" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']}>
                <MachineProductionLog />
              </ProtectedRoute>
            } />
            <Route path="workforce/payments" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','READ_ONLY']}>
                <WorkerPayments />
              </ProtectedRoute>
            } />
            <Route path="workforce/vendors" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','READ_ONLY']}>
                <VendorList />
              </ProtectedRoute>
            } />
            <Route path="workforce/resources" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','STAFF','READ_ONLY']}>
                <ResourceInventory />
              </ProtectedRoute>
            } />
            <Route path="workforce/equipment" element={
              <ProtectedRoute roles={['ADMIN','MANAGER','READ_ONLY']}>
                <EquipmentTracker />
              </ProtectedRoute>
            } />
            <Route path="workforce/reports" element={
              <ProtectedRoute roles={['ADMIN']}>
                <WorkforceReports />
              </ProtectedRoute>
            } />
            <Route path="workforce/sites" element={
              <ProtectedRoute roles={['ADMIN']}>
                <SiteManagement />
              </ProtectedRoute>
            } />

            {/* Workspace-wide payment settings — accessible from factory & other modules */}
            <Route path="settings/payment" element={
              <ProtectedRoute roles={['ADMIN']}>
                <PharmacySettings />
              </ProtectedRoute>
            } />

            {/* Tenant Admin — Chart of Accounts (workspace-specific financial accounts) */}
            <Route path="settings/chart-of-accounts" element={
              businessSetupEnabled ? (
                <ProtectedRoute roles={['ADMIN']}>
                  <TenantChartOfAccounts />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />

            {/* Tenant Admin — Master Types (workspace-specific lookup values) */}
            <Route path="settings/master-types" element={
              businessSetupEnabled ? (
                <ProtectedRoute roles={['ADMIN']}>
                  <TenantMasterTypes />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />

            {/* Tenant Admin — Super Admin approved navigation terminology */}
            <Route path="settings/terminology" element={
              terminologyControlsEnabled ? (
                <ProtectedRoute roles={['ADMIN']}>
                  <TenantTerminology />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />

            {/* Tenant Admin — Referral / Promo Codes */}
            <Route path="settings/referral" element={
              referralEnabled ? (
                <MyReferral />
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />

            <Route path="settings/barcode-qr" element={
              barcodeQrGeneratorEnabled ? (
                <ProtectedRoute roles={['ADMIN']}>
                  <BarcodeQrGenerator />
                </ProtectedRoute>
              ) : (
                <Navigate to={defaultAppRoute} replace />
              )
            } />

            <Route path="settings/cleanup-data" element={
              <ProtectedRoute roles={['ADMIN']}>
                <TenantDataCleanup />
              </ProtectedRoute>
            } />
            {/* Tenant Admin — Language Preferences (shown when tenant i18n is enabled) */}
            <Route path="settings/language-preferences" element={
              <ProtectedRoute roles={['ADMIN']}>
                <LanguagePreferences />
              </ProtectedRoute>
            } />

            {/* Catch-all inside layout */}
            <Route path="*" element={<Navigate to={defaultAppRoute} replace />} />
          </Route>

          {/* Global catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
